import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { BP_MOBILE } from "./canonical/breakpoints";

// PAGE_MAX_WIDTH — mantido por retrocompat; o canônico é var(--page-max-width) = 1440px.
// Páginas legacy seguem usando 1350px até migrarem.
export const PAGE_MAX_WIDTH = "1350px";

// =====================================================================
// getToneTokens — retorna background/border/text por tone (Bloco D)
// =====================================================================
// Refatorado em 2026-XX-XX (Sprint 1): aponta direto pra var(--*-bg|border|text)
// canônicos. Tones legados (purple, lime, orange, today, wood) ficam como
// aliases pros tones canônicos (info/success/warning/danger/neutral).
//
// Tones canônicos do Design System Bloco D:
//   info     — azul
//   success  — verde
//   warning  — amarelo/laranja
//   danger   — vermelho
//   neutral  — cinza
//
// Aliases legados (manter até módulos migrarem):
//   purple → info-like (era usado pra orçamentos em revisão / status visual)
//   lime   → success
//   orange → warning
//   today  → warning (destaque temporal)
//
// O 4o retorno (`accent`) é novo: tones podem precisar de cor de accent
// (ex.: chip de filtro ativo). Default = accent canônico.
export const getToneTokens = (_theme, tone = "default") => {
  switch (tone) {
    case "info":
      return {
        background: "var(--info-bg)",
        border: "var(--info-border)",
        text: "var(--info-text)",
        accent: "var(--info-solid)",
      };
    case "success":
    case "lime":
      return {
        background: "var(--success-bg)",
        border: "var(--success-border)",
        text: "var(--success-text)",
        accent: "var(--success-solid)",
      };
    case "warning":
    case "orange":
    case "today":
      return {
        background: "var(--warning-bg)",
        border: "var(--warning-border)",
        text: "var(--warning-text)",
        accent: "var(--warning-solid)",
      };
    case "danger":
      return {
        background: "var(--danger-bg)",
        border: "var(--danger-border)",
        text: "var(--danger-text)",
        accent: "var(--danger-solid)",
      };
    case "neutral":
      return {
        background: "var(--neutral-bg)",
        border: "var(--neutral-border)",
        text: "var(--neutral-text)",
        accent: "var(--neutral-solid)",
      };
    // ADR-0069: tons proprios de "Aprovado" (violet) e "Aguardando
    // entrega" (teal) na fila do comprador.
    case "violet":
      return {
        background: "var(--violet-bg)",
        border: "var(--violet-border)",
        text: "var(--violet-text)",
        accent: "var(--violet-solid)",
      };
    case "teal":
      return {
        background: "var(--teal-bg)",
        border: "var(--teal-border)",
        text: "var(--teal-text)",
        accent: "var(--teal-solid)",
      };
    case "purple":
      // Legacy alias — usar accent-soft (violet) como background
      return {
        background: "var(--accent-soft)",
        border: "var(--accent-soft-2)",
        text: "var(--accent)",
        accent: "var(--accent)",
      };
    default:
      return {
        background: "var(--surface)",
        border: "var(--border)",
        text: "var(--ink)",
        accent: "var(--accent)",
      };
  }
};

const metricCardCss = css`
  ${({ $tone = "default" }) => {
    const tokens = getToneTokens(null, $tone);

    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
    `;
  }}

  border-radius: 12px;
  padding: 16px 18px;
  min-width: 0;
  overflow: hidden;
`;

export const PageContainer = styled.div.attrs({ "data-page-container": "true" })`
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: ${BP_MOBILE}px) {
    padding: 20px 16px;
  }
`;

export const PageState = styled.div`
  width: 100%;
  max-width: ${PAGE_MAX_WIDTH};
  margin: 0 auto;
  padding: 24px;
  color: ${({ $tone = "default" }) =>
    $tone === "error" ? "var(--danger-text)" : "var(--ink-2)"};

  @media (max-width: ${BP_MOBILE}px) {
    padding: 20px 16px;
  }
`;

export const PageTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;

  @media (max-width: ${BP_MOBILE}px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const PageTitle = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 2vw, 1.9rem);
  font-weight: 700;
  color: var(--ink);
`;

export const PageActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;

  @media (max-width: ${BP_MOBILE}px) {
    flex-wrap: wrap;
    width: 100%;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionTitle = styled.h2`
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-2);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 12px;
`;

export const MetricCard = styled.div`
  ${metricCardCss}
`;

export const MotionMetricCard = styled(motion.div)`
  ${metricCardCss}
`;

export const MetricLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--ink-2);
  margin-bottom: 6px;
`;

export const MetricValue = styled.div`
  font-size: clamp(1.45rem, 2.6vw, 1rem);
  line-height: 1.1;
  font-weight: 700;
  color: ${({ $tone = "default" }) => getToneTokens(null, $tone).text};
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const MetricSub = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: var(--ink-2);
  overflow-wrap: anywhere;
`;

export const Tabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--border);
`;

export const TabButton = styled.button`
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  color: ${({ $active }) => ($active ? "var(--accent)" : "var(--ink-2)")};
  border-bottom: 2px solid
    ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  cursor: pointer;
  margin-bottom: -1px;
  transition: all 0.15s ease;

  &:hover {
    color: var(--ink);
  }
`;

export const TableWrapper = styled.div`
  /* overflow-x: clip em desktop (mantém sticky <th> funcionando).
     Em mobile (< 768px), em vez de scroll horizontal, os módulos
     migrados devem usar <CardList> via <ListView> — então não há
     fallback de overflow:auto. Páginas legacy ainda podem comprimir;
     refatoração pra ListView entra na Sprint correspondente. */
  overflow-x: clip;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: var(--surface);
`;

export const EmptyText = styled.p`
  color: var(--ink-2);
  font-size: 13px;
`;
